/**
 * Library package hooks — list, expanded detail (drawer load) and the write
 * mutations. Same coarse invalidation contract as the set hooks.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { libraryKeys } from '../keys';
import {
  createPackage,
  deletePackage,
  getPackage,
  listPackages,
  updatePackage,
} from '../client/packages-api';
import type { PackageWriteBody } from '../client/packages-api';

export function useLibraryPackages(enabled = true) {
  return useQuery({
    queryKey: libraryKeys.packages(),
    queryFn: listPackages,
    enabled,
    staleTime: 60_000,
  });
}

export function usePackageDetail(id: string | null) {
  return useQuery({
    queryKey: libraryKeys.package(id ?? 'none'),
    queryFn: () => getPackage(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function usePackageMutations() {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: libraryKeys.all });
  const create = useMutation({
    mutationFn: (
      body: PackageWriteBody & {
        name: string;
        formulaSetId: string;
        ruleSetId: string;
      },
    ) => createPackage(body),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: PackageWriteBody }) =>
      updatePackage(input.id, input.body),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deletePackage(id),
    onSuccess: invalidate,
  });
  return { create, update, remove };
}
